import React, { useEffect, useState } from 'react';
import { visitAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PersonSelect from '../../components/PersonSelect';

const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7',
  borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' };
const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: '#5e4a53',
  marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' };

const PRESETS = [
  { label: '3×/week (Mon, Wed, Fri)', rrule: 'FREQ=WEEKLY;BYDAY=MO,WE,FR' },
  { label: 'Daily (Mon–Fri)', rrule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR' },
  { label: 'Weekly (Mon)', rrule: 'FREQ=WEEKLY;BYDAY=MO' },
  { label: 'Bi-weekly', rrule: 'FREQ=WEEKLY;INTERVAL=2;BYDAY=MO' },
  { label: 'Monthly', rrule: 'FREQ=MONTHLY' },
];

const fmtErr = (d, fb) => {
  if (!d) return fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc||[]).slice(-1)[0]}: ${e.msg}` : JSON.stringify(e)).join('; ');
  return d.msg || d.message || JSON.stringify(d);
};

export default function RecurringVisits() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const canCreate = roles.includes('ADMIN') || roles.includes('DOCTOR');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const BLANK = {
    patient_id: '', service_type: 'NURSE_VISIT',
    recurrence_rule: PRESETS[0].rrule, start_date: '', scheduled_time: '09:00',
    duration_minutes: 60, preferred_nurse_id: '', end_date: '',
  };
  const [form, setForm] = useState(BLANK);
  const [preset, setPreset] = useState(0);

  async function load() {
    setLoading(true);
    try {
      const r = await visitAPI.listTemplates();
      setTemplates(r?.data?.data?.templates || r?.data?.templates || []);
    } catch {
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = {
        patient_id: form.patient_id,
        service_type: form.service_type,
        recurrence_rule: form.recurrence_rule,
        start_date: form.start_date,
        scheduled_time: form.scheduled_time,
        duration_minutes: Number(form.duration_minutes),
      };
      if (form.preferred_nurse_id) payload.preferred_nurse_id = form.preferred_nurse_id;
      if (form.end_date) payload.end_date = form.end_date;
      const r = await visitAPI.createTemplate(payload);
      const d = r?.data?.data || r?.data || {};
      setSuccess(`Template created — ${d.occurrences_generated} occurrences generated.`);
      setShowCreate(false);
      setForm(BLANK);
      load();
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail, 'Failed to create template'));
    } finally {
      setSaving(false);
    }
  }

  function handlePreset(i) {
    setPreset(i);
    setForm(f => ({ ...f, recurrence_rule: PRESETS[i].rrule }));
  }

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Inter, sans-serif', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2b1b22', margin: 0 }}>
          Recurring Visit Templates
        </h1>
        {canCreate && (
          <button onClick={() => { setShowCreate(true); setError(''); setSuccess(''); }} style={{
            padding: '9px 18px', background: 'linear-gradient(180deg,#94335a,#7b1e3c)',
            color: '#fff', border: 'none', borderRadius: 8, fontSize: 13,
            fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          }}>+ New Template</button>
        )}
      </div>

      {success && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px',
          borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{success}</div>
      )}

      {/* Create form */}
      {showCreate && (
        <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12,
          padding: 24, marginBottom: 24, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#2b1b22', marginTop: 0, marginBottom: 20 }}>
            Create Recurring Series
          </h2>
          {error && (
            <div style={{ background: '#fdecea', color: '#b71c1c', padding: '9px 13px',
              borderRadius: 8, marginBottom: 16, fontSize: 12.5 }}>{error}</div>
          )}
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={lbl}>Patient *</label>
                <PersonSelect role="patient" value={form.patient_id} required
                  onChange={id => setForm(f => ({ ...f, patient_id: id }))} />
              </div>
              <div>
                <label style={lbl}>Service Type *</label>
                <select style={inp} value={form.service_type}
                  onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))}>
                  {['NURSE_VISIT','DOCTOR_ASSESSMENT','PHYSIOTHERAPY','WOUND_CARE','PALLIATIVE'].map(t =>
                    <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                  )}
                </select>
              </div>
              <div>
                <label style={lbl}>Start Date *</label>
                <input type="date" style={inp} value={form.start_date} required
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>End Date (optional)</label>
                <input type="date" style={inp} value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Scheduled Time *</label>
                <input type="time" style={inp} value={form.scheduled_time} required
                  onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Duration (minutes)</label>
                <input type="number" style={inp} value={form.duration_minutes} min={15} max={480}
                  onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Recurrence Pattern *</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {PRESETS.map((p, i) => (
                  <button key={i} type="button" onClick={() => handlePreset(i)} style={{
                    padding: '5px 12px', borderRadius: 20, border: '1px solid #ece3e7',
                    background: preset === i ? '#7b1e3c' : '#f9f5f6',
                    color: preset === i ? '#fff' : '#2b1b22',
                    fontSize: 12, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                  }}>{p.label}</button>
                ))}
              </div>
              <input style={{ ...inp, fontFamily: 'monospace', fontSize: 12 }}
                value={form.recurrence_rule}
                onChange={e => { setForm(f => ({ ...f, recurrence_rule: e.target.value })); setPreset(-1); }}
                placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR" />
              <div style={{ fontSize: 11, color: '#8b7681', marginTop: 4 }}>
                iCal RRULE format. Visits will be generated 90 days ahead.
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Preferred Nurse (optional)</label>
              <PersonSelect role="nurse" value={form.preferred_nurse_id}
                onChange={id => setForm(f => ({ ...f, preferred_nurse_id: id }))}
                placeholder="— Any available nurse —" />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" disabled={saving} style={{
                padding: '10px 20px', background: saving ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 8, fontSize: 13,
                fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif',
              }}>{saving ? 'Creating…' : 'Create Series'}</button>
              <button type="button" onClick={() => setShowCreate(false)} style={{
                padding: '10px 20px', background: '#f3f4f6', color: '#374151',
                border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Templates list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b7681' }}>Loading templates…</div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b7681', background: '#fff',
          borderRadius: 12, border: '1px solid #ece3e7' }}>
          No recurring visit templates yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {templates.map(t => (
            <div key={t.template_id} style={{
              background: '#fff', border: '1px solid #ece3e7', borderRadius: 12,
              padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#2b1b22', marginBottom: 4 }}>
                    {t.service_type?.replace(/_/g, ' ')}
                    {' '}
                    <span style={{ fontWeight: 400, fontSize: 12, color: '#8b7681' }}>
                      {t.patient_name || t.patient_id?.slice(0, 8)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#5e4a53', fontFamily: 'monospace' }}>
                    {t.recurrence_rule}
                  </div>
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: t.status === 'ACTIVE' ? '#dcfce7' : '#fee2e2',
                  color: t.status === 'ACTIVE' ? '#166534' : '#991b1b',
                }}>{t.status}</span>
              </div>
              <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 12, color: '#8b7681' }}>
                <span>Start: {t.start_date}</span>
                {t.end_date && <span>End: {t.end_date}</span>}
                <span>Time: {t.scheduled_time}</span>
                <span>Duration: {t.duration_minutes} min</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
